import { z } from 'zod';

// ABI signatures mirror contracts/launchpad/src. The prototype is not a deployed release.
export const launchpadEventAbi = [
  { type: 'event', name: 'QuoteConfigured', inputs: [
    { name: 'quote', type: 'address', indexed: true }, { name: 'decimals', type: 'uint8', indexed: false },
    { name: 'enabled', type: 'bool', indexed: false },
  ] },
  { type: 'event', name: 'PresetConfigured', inputs: [
    { name: 'hash', type: 'bytes32', indexed: true }, { name: 'enabled', type: 'bool', indexed: false },
  ] },
  { type: 'event', name: 'PolicyConfigured', inputs: [
    { name: 'hash', type: 'bytes32', indexed: true }, { name: 'enabled', type: 'bool', indexed: false },
  ] },
  { type: 'event', name: 'WrappedNativeConfigured', inputs: [
    { name: 'wrapper', type: 'address', indexed: true }, { name: 'codeHash', type: 'bytes32', indexed: false },
  ] },
  { type: 'event', name: 'LaunchFirstBuy', inputs: [
    { name: 'curve', type: 'address', indexed: true }, { name: 'creator', type: 'address', indexed: true },
    { name: 'recipient', type: 'address', indexed: true }, { name: 'nativeFunding', type: 'bool', indexed: false },
    { name: 'maximumGross', type: 'uint128', indexed: false }, { name: 'spentGross', type: 'uint128', indexed: false },
    { name: 'netQuote', type: 'uint128', indexed: false }, { name: 'feeQuote', type: 'uint128', indexed: false },
    { name: 'refund', type: 'uint128', indexed: false }, { name: 'baseOut', type: 'uint128', indexed: false },
  ] },
  { type: 'event', name: 'GraduationAdapterConfigured', inputs: [
    { name: 'adapter', type: 'address', indexed: true }, { name: 'codeHash', type: 'bytes32', indexed: false },
  ] },
  { type: 'event', name: 'Graduated', inputs: [
    { name: 'locker', type: 'address', indexed: true }, { name: 'hook', type: 'address', indexed: true },
    { name: 'poolId', type: 'bytes32', indexed: true }, { name: 'baseUsed', type: 'uint128', indexed: false },
    { name: 'quoteUsed', type: 'uint128', indexed: false }, { name: 'baseDust', type: 'uint128', indexed: false },
    { name: 'quoteDust', type: 'uint128', indexed: false },
  ] },
  { type: 'event', name: 'LaunchCreated', inputs: [
    { name: 'curve', type: 'address', indexed: true }, { name: 'token', type: 'address', indexed: true },
    { name: 'creator', type: 'address', indexed: true }, { name: 'quote', type: 'address', indexed: false },
    { name: 'presetHash', type: 'bytes32', indexed: false }, { name: 'policyHash', type: 'bytes32', indexed: false },
    { name: 'metadataHash', type: 'bytes32', indexed: false },
  ] },
  { type: 'event', name: 'LaunchInitialized', inputs: [
    { name: 'fixedSupply', type: 'uint128', indexed: false }, { name: 'netQuoteTarget', type: 'uint128', indexed: false },
    { name: 'quoteDecimals', type: 'uint8', indexed: false }, { name: 'name', type: 'string', indexed: false }, { name: 'symbol', type: 'string', indexed: false },
  ] },
  { type: 'event', name: 'CreatorRecipientConfigured', inputs: [
    { name: 'slot', type: 'uint256', indexed: true }, { name: 'recipient', type: 'address', indexed: true }, { name: 'weightBps', type: 'uint16', indexed: false },
  ] },
  { type: 'event', name: 'Trade', inputs: [
    { name: 'payer', type: 'address', indexed: true }, { name: 'recipient', type: 'address', indexed: true },
    { name: 'isBuy', type: 'bool', indexed: false }, { name: 'baseAmount', type: 'uint128', indexed: false },
    { name: 'grossQuote', type: 'uint128', indexed: false }, { name: 'netQuote', type: 'uint128', indexed: false },
    { name: 'feeQuote', type: 'uint128', indexed: false }, { name: 'refund', type: 'uint128', indexed: false },
    { name: 'tier', type: 'uint256', indexed: false }, { name: 'feeBps', type: 'uint16', indexed: false },
  ] },
  { type: 'event', name: 'GraduationReady', inputs: [
    { name: 'quoteReserve', type: 'uint128', indexed: false }, { name: 'remainingBaseInventory', type: 'uint128', indexed: false },
  ] },
  { type: 'event', name: 'FeesAccrued', inputs: [
    { name: 'creatorAmount', type: 'uint256', indexed: false }, { name: 'buybackAmount', type: 'uint256', indexed: false }, { name: 'teamAmount', type: 'uint256', indexed: false },
  ] },
  { type: 'event', name: 'CreatorFeeClaimed', inputs: [
    { name: 'slot', type: 'uint256', indexed: true }, { name: 'recipient', type: 'address', indexed: true }, { name: 'amount', type: 'uint256', indexed: false },
  ] },
  { type: 'event', name: 'PlatformFeeClaimed', inputs: [
    { name: 'buyback', type: 'bool', indexed: true }, { name: 'recipient', type: 'address', indexed: true }, { name: 'amount', type: 'uint256', indexed: false },
  ] },
  { type: 'event', name: 'RecipientReplacementProposed', inputs: [
    { name: 'slot', type: 'uint256', indexed: true }, { name: 'oldRecipient', type: 'address', indexed: true }, { name: 'newRecipient', type: 'address', indexed: true },
    { name: 'nonce', type: 'uint256', indexed: false }, { name: 'deadline', type: 'uint256', indexed: false }, { name: 'proposalHash', type: 'bytes32', indexed: false },
  ] },
  { type: 'event', name: 'RecipientReplacementCancelled', inputs: [
    { name: 'slot', type: 'uint256', indexed: true }, { name: 'proposalHash', type: 'bytes32', indexed: true },
  ] },
  { type: 'event', name: 'RecipientReplaced', inputs: [
    { name: 'slot', type: 'uint256', indexed: true }, { name: 'oldRecipient', type: 'address', indexed: true }, { name: 'newRecipient', type: 'address', indexed: true },
    { name: 'proposalHash', type: 'bytes32', indexed: false }, { name: 'settledQuote', type: 'uint256', indexed: false }, { name: 'feeCheckpoint', type: 'uint256', indexed: false },
  ] },
] as const;

const projectionAddress = z.string().regex(/^0x[a-f0-9]{40}$/);
const projectionHash = z.string().regex(/^0x[a-f0-9]{64}$/);
const projectionAmount = z.string().regex(/^(0|[1-9][0-9]*)$/).max(78);
export const launchProjectionSchema = z.strictObject({
  schemaVersion: z.literal(1), launchId: projectionAddress, factory: projectionAddress,
  token: projectionAddress, creator: projectionAddress, quote: projectionAddress,
  presetHash: projectionHash, policyHash: projectionHash, metadataHash: projectionHash,
  name: z.string().max(1024), symbol: z.string().max(256), quoteDecimals: z.number().int().min(0).max(18),
  baseDecimals: z.number().int().min(0).max(255).nullable().default(null),
  fixedSupply: projectionAmount, graduationTarget: projectionAmount, netQuoteReserve: projectionAmount,
  state: z.enum(['CurveActive', 'ReadyToGraduate', 'Graduated']),
  graduation: z.strictObject({
    adapter: projectionAddress, adapterCodeHash: projectionHash, manager: projectionAddress,
    managerCodeHash: projectionHash, locker: projectionAddress, lockerCodeHash: projectionHash,
    hook: projectionAddress, hookCodeHash: projectionHash, poolId: projectionHash,
    lockedLiquidity: projectionAmount, baseUsed: projectionAmount, quoteUsed: projectionAmount,
    baseDust: projectionAmount, quoteDust: projectionAmount,
    transactionHash: projectionHash, blockNumber: projectionAmount, blockHash: projectionHash,
    logIndex: projectionAmount,
  }).nullable().default(null),
  recipients: z.array(z.strictObject({ slot: z.number().int().min(0).max(4), recipient: projectionAddress,
    weightBps: z.number().int().positive().max(10000), claimable: projectionAmount, claimed: projectionAmount })).min(1).max(5),
  creatorAccrued: projectionAmount, buybackAccrued: projectionAmount, teamAccrued: projectionAmount,
  buybackClaimed: projectionAmount, teamClaimed: projectionAmount,
  deploymentTx: projectionHash, sourceBlockNumber: projectionAmount, sourceBlockHash: projectionHash,
  sourceUpdatedAt: z.iso.datetime(), observedAt: z.iso.datetime(),
}).superRefine((value, ctx) => {
  if ((value.state === 'Graduated') !== (value.graduation !== null))
    ctx.addIssue({ code: 'custom', message: 'Graduated state requires verified graduation provenance', path: ['graduation'] });
});
export type LaunchProjection = z.infer<typeof launchProjectionSchema>;
